import { ref, computed, watch, nextTick } from 'vue';
import { ComponentState } from 'ihunter-pathfinder-components';

export function useTextbox(props, emit, inputModel) {
    const inputRef = ref(null);
    const uniqueId = ref(crypto.randomUUID());

    const currentValidationState = ref(ComponentState.Default);
    const customValidationMessage = ref('');
    const notifyType = computed(() => currentValidationState.value);

    const notifyMessage = computed(() => {
        if (currentValidationState.value === ComponentState.Critical) {
            return customValidationMessage.value;
        }
        if (currentValidationState.value === ComponentState.Success) {
            return props.successMessage || props.defaultMessage;
        }
        return props.defaultMessage;
    });

    const hasInput = computed(() => !!inputModel.value);
    const focusState = ref(false);
    const isReadonly = ref(props.readonly);
    const isDisabled = computed(() => props.disabled);
    const characterCount = computed(() => inputModel.value?.length || 0);

    const doFocus = () => {
        if (isReadonly.value) {
            isReadonly.value = false;
        }
        if (inputRef.value) {
            inputRef.value.focus();
            focusState.value = true;
        }
    };

    const onClick = async () => {
        emit('click');
        if (isReadonly.value) {
            isReadonly.value = false;
            await nextTick();
        }
        doFocus();
    };

    const onFocus = (event) => {
        event.target.select();
        focusState.value = true;
        emit('focus');
    };

    const onBlur = (event) => {
        if (event?.relatedTarget?.id === 'clear') {
            event.preventDefault();
            return;
        }

        const hasValue = inputModel.value?.trim()?.length > 0;

        if (!hasValue) {
            isReadonly.value = true;
            currentValidationState.value = ComponentState.Default;
        } else {
            isReadonly.value = false;
        }

        focusState.value = false;
        emit('blur');
    };

    const onChange = () => {
        emit('change', inputModel.value);
    };

    const onEnter = () => {
        emit('enter', inputModel.value);
    };

    const clear = () => {
        inputModel.value = '';
        emit('input', '');
        currentValidationState.value = ComponentState.Default;
    };

    const invalidate = (message) => {
        onInvalid({ target: { validationMessage: message } });
    };

    const validate = (state, message) => {
        currentValidationState.value = state || ComponentState.Default;
        customValidationMessage.value = message;
    };

    const onMouseDown = (event) => {
        if (!isReadonly.value) {
            if (event.target.tagName !== 'INPUT' && event.target.tagName !== 'TEXTAREA') {
                event.preventDefault();
                doFocus();
            }
        }
    };

    const onInvalid = (event) => {
        if (event.preventDefault) {
            event.preventDefault();
        }
        currentValidationState.value = ComponentState.Critical;
        customValidationMessage.value = event.target.validationMessage;
    };

    let cachedMaxHeight = null;

    const adjustHeight = () => {
        if (!props.grow || !inputRef.value) return;

        const el = inputRef.value;

        if (cachedMaxHeight === null) {
            const styleHeight = parseInt(window.getComputedStyle(el).maxHeight, 10);
            cachedMaxHeight = isNaN(styleHeight) ? Infinity : styleHeight;
        }

        el.style.height = '1px';

        const contentHeight = el.scrollHeight;

        el.style.height = `${contentHeight}px`;

        if (contentHeight > cachedMaxHeight) {
            el.style.overflowY = 'auto';
        } else {
            el.style.overflowY = 'hidden';
        }
    };

    const onInput = (event) => {
        const inputElement = event.target;
        emit('input', inputElement.value);
        adjustHeight();

        if (inputElement.value.trim() === '') {
            currentValidationState.value = ComponentState.Default;
            return;
        }

        if (props.showInput) {
            if (!inputElement.validity.valid) {
                currentValidationState.value = ComponentState.Critical;
                customValidationMessage.value = inputElement.validationMessage;
            } else {
                currentValidationState.value = ComponentState.Success;
            }
        } else {
            currentValidationState.value = ComponentState.Default;
        }
    };

    return {
        inputRef,
        notifyMessage,
        notifyType,
        uniqueId,
        hasInput,
        focusState,
        isReadonly,
        isDisabled,
        characterCount,
        onClick,
        onFocus,
        onBlur,
        onChange,
        onEnter,
        clear,
        doFocus,
        invalidate,
        validate,
        onMouseDown,
        onInvalid,
        onInput,
        adjustHeight,
    };
}
